// src/tabs/messages/ModerationAvisView.jsx — Consultation LECTURE SEULE (déonto)
//
// Vue admin GLOBALE des avis voyageurs (sous-onglet MessagesPanel).
// Décision Dimitri (DGCCRF/déonto) : les avis s'affichent TELS QU'ÉCRITS par
// les voyageurs. AUCUNE action de modération DESTRUCTIVE (approuver / rejeter /
// masquer / réapprouver / éditer) — ces capacités ont été RETIRÉES.
//
// WAGON O (BUG 60b) — SEULE action admin autorisée : « Mettre en avant »
// (toggle `pinned`, étoile). NON destructif : ne change NI le statut NI le texte
// NI les stats publiques — uniquement l'ordre d'affichage (annonce ② tête de
// liste, vitrine ① sélection). Compatible ADR-183.
//
// Liste paginée 50/page + filtres (logement / source / status / rating) pour le
// browsing read-only. Affiche le review_text ORIGINAL (jamais de texte réécrit).
//
// Workflow API :
//   - GET  /api/sites?action=avis_admin_list&...filtres&page=N
//   - POST /api/sites?action=avis_set_pinned  body { avis_id, pinned }
//
// (Les actions avis_admin_update_status / avis_admin_update_text n'existent plus.)

import { useState, useEffect, useCallback, useMemo } from "react"
import { Star, Lock, RefreshCw, Home, StickyNote, Globe, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react"
import { supabase } from "../../supabase"
import { DS } from "../../lib/ui"
import { formatDateFR } from "../../lib/format"
import { getAllLogsFlat } from "../../lib/sousloc-helpers"
import AvisResponseCopilot from "../logements/AvisResponseCopilot"

const STATUS_OPTIONS = [
  { id: 'all',           label: 'Tous statuts', color: '#888' },
  { id: 'pending',       label: 'En attente',   color: DS.amber },
  { id: 'auto_approved', label: 'Approuvés',    color: DS.green },
]

const SOURCE_OPTIONS = [
  { id: 'all',     label: 'Toutes sources' },
  { id: 'airbnb',  label: 'Airbnb' },
  { id: 'booking', label: 'Booking' },
]

const RATING_OPTIONS = [
  { id: 'all', label: 'Toutes notes' },
  { id: '5',   label: '★★★★★' },
  { id: '4',   label: '★★★★' },
  { id: '3',   label: '★★★' },
  { id: '2',   label: '★★' },
  { id: '1',   label: '★' },
]

const STATUS_COLORS = {
  auto_approved: { bg: DS.successBg, fg: DS.successText, label: 'Approuvé' },
  pending:       { bg: DS.warningBg, fg: DS.warningText, label: 'En attente' },
  rejected:      { bg: DS.dangerBg,  fg: DS.dangerText,  label: 'Rejeté' },
  hidden:        { bg: '#e2e3e5',    fg: '#383d41',      label: 'Masqué' },
}

const SOURCE_BADGES = {
  airbnb:  { bg: '#fce4e8', fg: '#c13584', label: 'Airbnb' },
  booking: { bg: '#e3f1fb', fg: '#003580', label: 'Booking' },
}

function renderStars(rating) {
  const n = Math.round(Number(rating))
  // Note absente ou hors barème 1..5 → « sans note » (jamais 1 étoile par défaut).
  if (!Number.isFinite(n) || n < 1 || n > 5) return 'sans note'
  return '★'.repeat(n) + '☆'.repeat(5 - n)
}

export default function ModerationAvisView({ cfg }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [avis, setAvis] = useState([])
  const [total, setTotal] = useState(0)
  const [pinnedTotal, setPinnedTotal] = useState(0) // WAGON R5 — compteur sélection
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [pinningId, setPinningId] = useState(null) // WAGON O — avis en cours de toggle
  const [pinError, setPinError] = useState(null)
  const [filters, setFilters] = useState({
    logement_id: 'all',
    source: 'all',
    status: 'all',
    rating: 'all',
  })

  // Liste logements (cfg) pour dropdown filtre.
  const allLogs = useMemo(
    () => getAllLogsFlat(cfg || {}).map(l => ({ id: l.id, nom: l.nom })),
    [cfg],
  )

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')

      const params = new URLSearchParams({ action: 'avis_admin_list', page: String(page) })
      if (filters.logement_id !== 'all') params.set('logement_id', filters.logement_id)
      if (filters.source !== 'all')      params.set('source', filters.source)
      if (filters.status !== 'all')      params.set('status', filters.status)
      if (filters.rating !== 'all')      params.set('rating', filters.rating)

      const res = await fetch(`/api/sites?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (!res.ok || !json.ok) throw new Error(json.error || 'fetch_failed')

      setAvis((json.avis || []).filter(Boolean))
      setTotal(json.total || 0)
      setPinnedTotal(json.pinned_total || 0)
      setPageSize(json.page_size || 50)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [filters, page])

  useEffect(() => { refresh() }, [refresh])

  // WAGON O — toggle « Mettre en avant » (pinned). NON destructif : ne change
  // que l'ordre d'affichage public (annonce tête de liste, vitrine sélection).
  // Optimistic UI + rollback en cas d'échec serveur.
  const togglePinned = useCallback(async (avisItem) => {
    const next = !avisItem.pinned
    setPinError(null)
    setPinningId(avisItem.id)
    // Optimistic : on bascule localement avant la confirmation serveur (état
    // de l'avis ET compteur de sélection).
    setAvis(prev => prev.map(a => a.id === avisItem.id ? { ...a, pinned: next } : a))
    setPinnedTotal(prev => Math.max(0, prev + (next ? 1 : -1)))
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) throw new Error('Pas de session admin active.')

      const res = await fetch('/api/sites?action=avis_set_pinned', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avis_id: avisItem.id, pinned: next }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.ok) throw new Error(json.error || 'pin_failed')
      // Aligne sur la valeur serveur (source de vérité). Réconcilie le compteur
      // si le serveur a renvoyé un état différent de l'optimistic.
      const serverPinned = json.avis?.pinned === true
      setAvis(prev => prev.map(a => a.id === avisItem.id ? { ...a, pinned: serverPinned } : a))
      if (serverPinned !== next) {
        setPinnedTotal(prev => Math.max(0, prev + (serverPinned ? 1 : -1) - (next ? 1 : -1)))
      }
    } catch (e) {
      // Rollback de l'optimistic (avis ET compteur).
      setAvis(prev => prev.map(a => a.id === avisItem.id ? { ...a, pinned: avisItem.pinned } : a))
      setPinnedTotal(prev => Math.max(0, prev + (next ? -1 : 1)))
      setPinError(e.message)
    } finally {
      setPinningId(null)
    }
  }, [])

  // Reset page si filtres changent.
  function updateFilter(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div data-testid="moderation-avis-view" style={{ fontFamily: DS.fontBody }}>
      {/* Bandeau consultation seule */}
      <div data-testid="moderation-avis-readonly-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, background: DS.cielSoft, color: DS.marineDark, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, fontWeight: 500 }}>
        <Lock size={15} strokeWidth={2} style={{ flexShrink: 0 }}/>
        <span>Lecture seule — les avis s'affichent tels qu'écrits par les voyageurs.
        Seule la <strong>mise en avant</strong> est possible (ordre d'affichage ; note et compteur publics inchangés).</span>
      </div>

      {/* Header + filtres */}
      <div className="fbz-card" style={{ background: DS.card, borderRadius: DS.radiusCard, padding: 14, marginBottom: 14, border: `0.5px solid ${DS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, fontSize: 15 }}>
            <Star size={16} strokeWidth={2} color={DS.marine}/> Avis voyageurs
          </div>
          <div style={{ fontSize: 12, color: DS.textSecondary }}>
            {total} avis
          </div>
          {/* WAGON R5 (LOT 3) — compteur de sélection « mis en avant ». Visible
            * en permanence pour que la mise en avant soit évidente. */}
          <div
            data-testid="moderation-avis-pinned-counter"
            title="Avis sélectionnés comme témoignages (mis en avant)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, color: DS.warningText,
              background: DS.warningBg, border: `1px solid ${DS.warning}`,
              padding: '3px 10px', borderRadius: 12,
            }}
          >
            <Star size={13} strokeWidth={2} fill={DS.warning} color={DS.warning}/> {pinnedTotal} en avant
          </div>
          <button
            className="fbz-press"
            onClick={refresh}
            disabled={loading}
            data-testid="moderation-avis-refresh"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', padding: '7px 13px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: loading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: DS.fontBody }}
          >
            <RefreshCw size={14} strokeWidth={2}/> Actualiser
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            data-testid="moderation-avis-filter-logement"
            value={filters.logement_id}
            onChange={(e) => updateFilter('logement_id', e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
          >
            <option value="all">Tous logements</option>
            {allLogs.map(l => (
              <option key={l.id} value={l.id}>{l.id} — {l.nom}</option>
            ))}
          </select>

          <select
            data-testid="moderation-avis-filter-source"
            value={filters.source}
            onChange={(e) => updateFilter('source', e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
          >
            {SOURCE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>

          <select
            data-testid="moderation-avis-filter-status"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>

          <select
            data-testid="moderation-avis-filter-rating"
            value={filters.rating}
            onChange={(e) => updateFilter('rating', e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', fontSize: 12 }}
          >
            {RATING_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, borderLeft: `3px solid ${DS.danger}`, marginBottom: 10, fontSize: 12.5 }}>
          <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0 }}/> {error}
        </div>
      )}

      {pinError && (
        <div data-testid="moderation-avis-pin-error" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: DS.dangerBg, color: DS.dangerText, borderRadius: 8, borderLeft: `3px solid ${DS.danger}`, marginBottom: 10, fontSize: 12.5 }}>
          <AlertTriangle size={15} strokeWidth={2} style={{ flexShrink: 0 }}/> Mise en avant échouée : {pinError}
        </div>
      )}

      {loading && <div style={{ color: DS.textSecondary, padding: 10, fontSize: 12 }}>Chargement…</div>}

      {!loading && avis.length === 0 && (
        <div data-testid="moderation-avis-empty" style={{ padding: 24, textAlign: 'center', color: DS.textMuted, background: DS.card, borderRadius: DS.radiusCard, border: `0.5px solid ${DS.border}` }}>
          Aucun avis avec ces filtres.
        </div>
      )}

      {!loading && avis.map((r) => {
        const sc = STATUS_COLORS[r.status] || { bg: '#eee', fg: '#000', label: r.status }
        const sb = SOURCE_BADGES[r.source] || { bg: '#eee', fg: '#000', label: r.source }
        const displayText = r.review_text
        const logementNom = allLogs.find(l => l.id === r.logement_id)?.nom || r.logement_id
        return (
          <div
            key={r.id}
            className="fbz-card"
            data-testid={`moderation-avis-card-${r.id}`}
            style={{
              border: `0.5px solid ${DS.border}`,
              borderRadius: DS.radiusCard,
              padding: 14,
              marginBottom: 10,
              background: DS.card,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 14.5, color: DS.marineDark }}>
                  {r.guest_name || 'Anonyme'} <span style={{ color: DS.warning, marginLeft: 6 }}>{renderStars(r.rating)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: DS.textSecondary, marginTop: 3 }}>
                  <Home size={13} strokeWidth={2}/> <strong>{logementNom}</strong> · {formatDateFR(r.review_date)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* WAGON O — toggle « Mettre en avant » (pinned). Actionnable
                  * seulement sur les avis publics (auto_approved) : seuls eux
                  * remontent sur l'annonce / la vitrine. */}
                {r.status === 'auto_approved' && (
                  <button
                    type="button"
                    data-testid={`moderation-avis-pin-${r.id}`}
                    aria-pressed={r.pinned === true}
                    onClick={() => togglePinned(r)}
                    disabled={pinningId === r.id}
                    title={r.pinned ? 'Retirer de la mise en avant' : 'Mettre en avant (témoignage)'}
                    className="fbz-press"
                    style={{
                      padding: '5px 12px', borderRadius: DS.radiusControl, fontSize: 12, fontWeight: 700,
                      cursor: pinningId === r.id ? 'wait' : 'pointer',
                      border: `1px solid ${DS.warning}`,
                      background: r.pinned ? DS.warning : DS.card,
                      color: r.pinned ? '#fff' : DS.warningText,
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontFamily: DS.fontBody,
                    }}
                  >
                    <Star size={13} strokeWidth={2} fill={r.pinned ? '#fff' : 'none'}/> {r.pinned ? 'Mis en avant' : 'Mettre en avant'}
                  </button>
                )}
                <span style={{ padding: '3px 10px', borderRadius: 7, background: sb.bg, color: sb.fg, fontSize: 11, fontWeight: 700 }}>
                  {sb.label}
                </span>
                <span style={{ padding: '3px 10px', borderRadius: 7, background: sc.bg, color: sc.fg, fontSize: 11, fontWeight: 700 }}>
                  {sc.label}
                </span>
                {r.locale && r.locale !== 'fr' && (
                  <span
                    data-testid={`moderation-avis-locale-${r.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 7, background: DS.cielSoft, color: DS.marine, fontSize: 11, fontWeight: 700 }}
                  >
                    <Globe size={12} strokeWidth={2}/> {String(r.locale).toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            <div data-testid={`moderation-avis-text-${r.id}`} style={{ background: DS.cielSoft, padding: 10, borderRadius: 8, fontSize: 13, color: DS.marine, marginBottom: 8, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {displayText}
            </div>

            {r.admin_notes && (
              <div style={{ display: 'flex', gap: 6, fontSize: 11, color: DS.warningText, background: DS.warningBg, padding: 8, borderRadius: 6 }}>
                <StickyNote size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}/> <span><strong>Notes admin :</strong> {r.admin_notes}</span>
              </div>
            )}

            {/* Copilote de réponse — brouillon éphémère, ne modère/publie/sauve rien */}
            <AvisResponseCopilot avis={r} />
          </div>
        )
      })}

      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button
            className="fbz-press"
            data-testid="moderation-avis-prev"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1, fontSize: 12, fontWeight: 600, fontFamily: DS.fontBody }}
          >
            <ChevronLeft size={15} strokeWidth={2}/> Précédent
          </button>
          <div style={{ padding: '7px 12px', fontSize: 12, color: DS.textSecondary }}>
            Page {page} / {totalPages}
          </div>
          <button
            className="fbz-press"
            data-testid="moderation-avis-next"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: DS.radiusControl, border: `0.5px solid ${DS.borderHover}`, background: DS.card, color: DS.marine, cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1, fontSize: 12, fontWeight: 600, fontFamily: DS.fontBody }}
          >
            Suivant <ChevronRight size={15} strokeWidth={2}/>
          </button>
        </div>
      )}
    </div>
  )
}

// Exports internes pour tests Jest.
export const _test = { STATUS_OPTIONS, SOURCE_OPTIONS, RATING_OPTIONS, STATUS_COLORS, SOURCE_BADGES, renderStars }
